// Signature block on signed documents (notes, visit summaries, discharge instructions): who signed, their
// credentials and when — "Signed by: <name>, <credentials> · <DD Mon YYYY, HH:mm>". Times are shown in the
// facility's time zone (REACT_APP_FACILITY_TIME_ZONE, default Africa/Lagos) whoever views or prints them.
// Pure: no API/axios imports.

export type DocumentSignature = {
  name: string | null
  credentials?: string | null
  signed_at?: string | null
  /** A signed note its signer changed afterwards: the line ends "· Edited <date, time>". */
  edited_at?: string | null
}

export const FACILITY_TIME_ZONE = process.env.REACT_APP_FACILITY_TIME_ZONE?.trim() || 'Africa/Lagos'

const formatterFor = (timeZone: string): Intl.DateTimeFormat => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: 'short',
    year: 'numeric'
  }
  try {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone })
  } catch {
    // An unknown zone name: fall back to the default rather than break the document.
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'Africa/Lagos' })
  }
}

/** The facility's date as YYYY-MM-DD (e.g. the latest allowed discharge date). */
export const facilityToday = (now: Date = new Date(), timeZone: string = FACILITY_TIME_ZONE): string => {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-CA', { ...options, timeZone })
  } catch {
    formatter = new Intl.DateTimeFormat('en-CA', { ...options, timeZone: 'Africa/Lagos' })
  }
  const parts = Object.fromEntries(formatter.formatToParts(now).map(part => [part.type, part.value]))
  return `${parts.year}-${parts.month}-${parts.day}`
}

/** "01 Oct 2026, 14:05" in the facility's time zone; null when missing or unreadable. */
export const formatSignedAt = (iso?: string | null, timeZone: string = FACILITY_TIME_ZONE): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const parts = Object.fromEntries(
    formatterFor(timeZone)
      .formatToParts(date)
      .map(part => [part.type, part.value])
  )
  return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${parts.minute}`
}

/** The signature line, or null when there is no signer to show (e.g. a draft). `label`: the translated page's fixed
 * label (utils/patientDocumentLabels) in place of "Signed by:". */
export const signatureLine = (signature?: DocumentSignature | null, label: string = 'Signed by:'): string | null => {
  const name = signature?.name?.trim()
  if (!name) return null
  const who = [name, signature?.credentials?.trim()].filter(Boolean).join(', ')
  const when = formatSignedAt(signature?.signed_at)
  const editedAt = formatSignedAt(signature?.edited_at)
  const edited = editedAt ? `Edited ${editedAt}` : null
  return `${label} ${[who, when, edited].filter(Boolean).join(' · ')}`
}
